import { signOut } from "firebase/auth";
import { auth } from "../firebaseConfig";
import { useUserStore } from "../store/userStore";
import axios from "axios";
import { clearNextPath } from "./nextPath";
import { runSessionCleanups } from "./sessionCleanup";
import { clearSandboxView } from "../features/sandbox/sandboxView";

// Este archivo es IDÉNTICO en CC-CONSOLE y WAYAK (interceptor de sesión
// unido). Lo que sólo existe en un fork se engancha desde fuera (ver
// `onSessionCleared` en utils/sessionCleanup.ts), nunca con un import que en
// el otro no exista.
export { onSessionCleared } from "./sessionCleanup";

const API_URL = import.meta.env.VITE_FIREBASE_API_URL;

// Canal entre pestañas del mismo navegador: al cerrar sesión en una, las demás
// también salen (ver utils/remoteLogout.ts).
export const AUTH_CHANNEL = "auth";
export const SESSION_CLOSED_MESSAGE = "session_closed";

/**
 * Por qué se cerró la sesión; viaja en el aviso entre pestañas para que las
 * demás muestren el texto correcto:
 *   • "logout": la persona cerró sesión (o el token venció en esta pestaña).
 *   • "remote": se cerró desde otro dispositivo o por un administrador.
 *   • "expired": el servidor rechazó la sesión (expirada o ya cerrada).
 */
export type SessionClosedReason = "logout" | "remote" | "expired";

const notifyOtherTabsOfLogout = (reason: SessionClosedReason) => {
  try {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.postMessage({ type: SESSION_CLOSED_MESSAGE, reason });
    channel.close();
  } catch (error) {
    console.warn("BroadcastChannel not available:", error);
  }
};

// Flag global para evitar peticiones durante el logout
let _isLoggingOut = false;

export const isLoggingOut = (): boolean => _isLoggingOut;
export const setLoggingOut = (value: boolean): void => {
  _isLoggingOut = value;
};

export const decodeJWT = (token: string): any | null => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000;
  const now = Date.now();

  const bufferTime = 5 * 60 * 1000;
  return now >= expirationTime - bufferTime;
};

export const getTokenTimeRemaining = (token: string): number => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return 0;

  const expirationTime = decoded.exp * 1000;
  const remaining = expirationTime - Date.now();
  return remaining > 0 ? remaining : 0;
};

export const getTokenExpirationDate = (token: string): Date | null => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return null;
  return new Date(decoded.exp * 1000);
};

export const formatTimeRemaining = (token: string): string => {
  const remaining = getTokenTimeRemaining(token);
  if (remaining <= 0) return "Expirado";

  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

/**
 * Cierra la sesión local (y, si `closeBackendSession`, también la del backend).
 *
 * @param reason por qué se cierra, para el aviso a las demás pestañas. Si no se
 *   da: "logout" cuando se cierra también en el backend y "expired" cuando no
 *   (la sesión ya no valía allá).
 */
export const clearSession = async (
  closeBackendSession = true,
  reason: SessionClosedReason = closeBackendSession ? "logout" : "expired",
) => {
  // Setear flag para evitar peticiones durante el logout
  _isLoggingOut = true;

  const { setUser } = useUserStore.getState();

  const token = localStorage.getItem("token");
  const sessionId = localStorage.getItem("sessionId");

  const fcmToken = localStorage.getItem("tokenCFM");
  const fcmUserId = localStorage.getItem("userId");
  if (closeBackendSession && token && sessionId && fcmToken && fcmUserId) {
    try {
      await axios.post(
        `${API_URL}/notifications/unsuscribe`,
        { tokenCFM: fcmToken, id: fcmUserId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-session-id": sessionId,
            "x-user-id": fcmUserId,
          },
        },
      );
    } catch (error) {
      console.error("Error unsubscribing FCM token on logout:", error);
    }
  }

  if (closeBackendSession && sessionId && token) {
    try {
      // Cuerpo `{}` y no `null`: con `null` el body-parser estricto del
      // emulador responde 400 y la sesión local se cierra sola.
      await axios.put(`${API_URL}/session/close`, {}, {
        params: { id: sessionId, sessionId: sessionId },
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error("Error closing session in backend:", error);
    }
  }

  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out from Firebase:", error);
  }

  // Las demás pestañas comparten el token que se va a borrar: que salgan. Se
  // avisa ANTES de borrar para que el motivo les llegue antes que el evento
  // `storage` del token (que no lo lleva).
  notifyOtherTabsOfLogout(reason);

  console.log("Limpiando localStorage desde clearSession");
  // Un `next` de vincular pendiente (CC-CONSOLE#1561) no lo hereda la
  // siguiente sesión de la pestaña.
  clearNextPath();
  localStorage.removeItem("token");
  localStorage.removeItem("sessionId");
  localStorage.removeItem("lastRoute");
  localStorage.removeItem("companyId");
  localStorage.removeItem("userCompanyId");
  localStorage.removeItem("userCompanyName");
  clearSandboxView();
  // Se borran junto con el resto de la sesión: son el respaldo del que lee
  // analytics/identity.ts, y si sobreviven al logout el siguiente usuario en
  // el mismo equipo puede quedar identificado con el rol del anterior.
  localStorage.removeItem("userRole");
  localStorage.removeItem("userRoleId");
  localStorage.removeItem("userIsPlatformAdmin");
  // Derechos de la sesión (CC-CONSOLE #649): no deben pasar al siguiente
  // usuario. En un fork sin ese store la clave no existe y no pasa nada.
  localStorage.removeItem("entitlements");
  localStorage.removeItem("vehicle-route-tracking");
  localStorage.removeItem("vehicle-footprint-tracking");
  localStorage.removeItem("tokenCFM");
  localStorage.removeItem("tokenCFM_domain");
  localStorage.removeItem("tokenCFM_userId");

  // Lo que otros módulos guardan de la sesión (p. ej. los derechos de
  // CC-CONSOLE). Una limpieza que falla no deja a medias el cierre de sesión.
  runSessionCleanups();

  setUser(null);

  // Nota: No reseteamos _isLoggingOut aquí porque queremos que
  // permanezca true hasta que se redirija al login
};

export const forceLogout = async (redirect = true) => {
  await clearSession();

  if (redirect) {
    window.location.href = "/";
  }
};

// El cierre REMOTO (`forceRemoteLogout`, antes aquí en WAYAK) vive en
// utils/remoteLogout.ts junto con la escucha entre pestañas y el push FCM.

export const checkTokenValidity = (): boolean => {
  const token = localStorage.getItem("token");

  if (!token) {
    return false;
  }

  if (isTokenExpired(token)) {
    clearSession();
    return false;
  }

  return true;
};

export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem("token");
  if (!token) return false;
  return !isTokenExpired(token);
};

export const refreshToken = async (): Promise<boolean> => {
  const currentToken = localStorage.getItem("token");
  const sessionId = localStorage.getItem("sessionId");

  if (!currentToken) return false;

  try {
    // `x-session-id`: el backend valida ESTA sesión (validateActiveSession).
    // Sin él le basta con que el usuario tenga ALGUNA sesión activa, y una
    // sesión ya cerrada en otro dispositivo podía seguir renovando su token.
    const { data } = await axios.post(`${API_URL}/session/refresh`, {}, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
        "x-session-id": sessionId || "",
      },
    });

    const newToken =
      data?.data?.data?.token || data?.data?.token || data?.token;

    if (newToken) {
      localStorage.setItem("token", newToken);
      console.log("Token refreshed successfully");
      return true;
    }

    return false;
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    const key = axios.isAxiosError(error)
      ? (error.response?.data as any)?.meta?.messageKey
      : undefined;

    // Sesión cerrada remotamente o token inválido en el servidor
    if (
      status === 401 ||
      status === 403 ||
      key === "SESSION_CLOSED" ||
      key === "SESSION_NOT_FOUND"
    ) {
      console.warn("[refreshToken] Session invalid on server (SESSION_CLOSED):", key || status);
      return false; // El caller debe detectar esto y ejecutar logout
    }

    console.error("Error refreshing token:", error);
    return false;
  }
};

export const shouldRefreshToken = (token: string): boolean => {
  const timeRemaining = getTokenTimeRemaining(token);
  const oneDayInMs = 24 * 60 * 60 * 1000;
  return timeRemaining > 0 && timeRemaining < oneDayInMs;
};
